import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { message } from "antd";
import { Button2 } from "../index";
import { useAccounts } from "../../data/live";
import { accountsApi } from "../../api/endpoints";

const Centered = ({ children }) => (
  <div className="d-flex flex-column align-items-center justify-content-center gap-2 h-100">
    {children}
  </div>
);

const QrReceive = () => {
  const { accounts } = useAccounts();
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);

  // PayID is an AUD-domestic addressing service, so we use the AUD wallet.
  const audWallet = useMemo(
    () =>
      accounts.find((a) => a.currency === "AUD" && a.isDefault) ||
      accounts.find((a) => a.currency === "AUD"),
    [accounts]
  );

  useEffect(() => {
    if (!audWallet) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    accountsApi
      .depositInstructions(audWallet.id)
      .then((d) => !cancelled && setInstructions(d))
      .catch(() => !cancelled && setInstructions(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [audWallet]);

  const payid = instructions?.payid;
  const payidActive = instructions?.payidState === "active";

  // QR encodes the PayID when active (scan + pay). Until then it encodes the
  // bank-transfer details so the payer can still send funds with the reference.
  const qrValue =
    payidActive && payid
      ? payid
      : instructions
      ? [
          "ARM Financial, Pay me",
          `Account: ${instructions.accountName}`,
          `BSB: ${instructions.bsb}`,
          `Account Number: ${instructions.accountNumber}`,
          `Reference: ${instructions.reference}`,
        ].join("\n")
      : "";

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    message.success("Copied");
  };

  const handleShare = async () => {
    const text = payidActive
      ? `Pay me via PayID: ${payid}`
      : instructions
      ? `Pay me:\n${qrValue}`
      : "";
    if (navigator.share) {
      try {
        await navigator.share({ title: "ARM Financial Payment", text });
      } catch {
        /* user cancelled share */
      }
    } else {
      copy(text);
    }
  };

  if (loading)
    return (
      <Centered>
        <span className="fs14px">Loading…</span>
      </Centered>
    );
  if (!audWallet)
    return (
      <Centered>
        <span className="fs-5 fw-bold">No AUD wallet</span>
        <span className="fs14px" style={{ color: "var(--text-muted)" }}>
          Create an AUD wallet to receive payments.
        </span>
      </Centered>
    );

  return (
    <div className="d-flex flex-column align-items-center justify-content-center gap-3 h-100 py-4">
      {/* QR code (always on white for scanner contrast) */}
      <div
        className="p-3 rounded-3"
        style={{ background: "#fff", border: "1px solid var(--border-color)" }}
      >
        {qrValue ? (
          <QRCodeSVG value={qrValue} size={196} level="M" />
        ) : (
          <div style={{ width: 196, height: 196 }} />
        )}
      </div>

      <span className="fs-5 fw-bold text-center">
        {payidActive ? "Your PayID" : "Receive a Payment"}
      </span>

      {payidActive ? (
        <>
          <div
            className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
            style={{ background: "rgba(27,187,107,0.1)" }}
          >
            <span className="fw-semibold" style={{ color: "var(--accent-color)" }}>
              {payid}
            </span>
            <span
              className="fs12px cursor-pointer"
              style={{ color: "var(--accent-color)" }}
              onClick={() => copy(payid)}
            >
              Copy
            </span>
          </div>
          <span
            className="fs14px text-center"
            style={{ color: "var(--text-muted)", maxWidth: 320 }}
          >
            Show this QR or share your PayID. Anyone can pay you from their
            banking app, funds land in your AUD wallet automatically.
          </span>
        </>
      ) : (
        <>
          <span
            className="fs14px text-center"
            style={{ color: "var(--text-muted)", maxWidth: 340 }}
          >
            {instructions?.payid
              ? "Your PayID is being activated. Meanwhile, share these bank details, payments with your reference are credited automatically."
              : "Share these bank-transfer details. Payments quoting your reference are credited to your AUD wallet automatically."}
          </span>
          {instructions && (
            <div
              className="d-flex flex-column gap-1 px-4 py-3 rounded-3 fs14px"
              style={{
                background: "var(--card-bg)",
                border: "1px solid var(--border-color)",
              }}
            >
              <div className="d-flex justify-content-between gap-4">
                <span style={{ color: "var(--text-muted)" }}>BSB</span>
                <span className="fw-medium">{instructions.bsb}</span>
              </div>
              <div className="d-flex justify-content-between gap-4">
                <span style={{ color: "var(--text-muted)" }}>Account</span>
                <span className="fw-medium">{instructions.accountNumber}</span>
              </div>
              <div className="d-flex justify-content-between gap-4">
                <span style={{ color: "var(--text-muted)" }}>Reference</span>
                <span className="fw-bold" style={{ color: "var(--accent-color)" }}>
                  {instructions.reference}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="custom-div">
        <Button2 buttonName={"Share"} className={"btn-dark"} onClick={handleShare} />
      </div>
    </div>
  );
};

export default QrReceive;
